// La pantalla dedicada del chat (`/chat`): la MISMA conversación que el panel
// lateral, a ancho completo, más la columna de respuestas gráficas a la derecha.
//
// No hay un segundo motor ni un segundo estado: los dos leen el mismo
// `ChatAtlasProvider`, así que se puede empezar a escribir en el panel, abrir
// esta pantalla y seguir en la misma línea (mensajes, borrador, adjuntos
// enganchados e interruptores incluidos).
//
// ESTA ES LA ÚNICA PANTALLA QUE DIBUJA GRÁFICOS. En el panel lateral no hay
// ancho (~420px) y partirlo en dos sería ilegible, así que allá la tarjeta de un
// gráfico avisa y trae al usuario acá. Ver `PanelGraficosChat`.

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { RotateCcw } from 'lucide-react';
import { useChatAtlas } from '../ChatAtlasContext';
import { graficoQueSeAbreSolo, ultimoGrafico } from '../graficosChat';
import { ConversacionChatAtlas } from './ConversacionChatAtlas';
import { PanelGraficosChat } from './PanelGraficosChat';
import { vieneDelPanel } from './rutaChatAtlas';
import { useEsEscritorio } from './useEsEscritorio';

export const PantallaChatAtlas: React.FC = () => {
  const chat = useChatAtlas();
  const esEscritorio = useEsEscritorio();

  // CÓMO SE LLEGÓ ACÁ CAMBIA LO QUE HAY QUE HACER (ver `EntradaAPantalla`):
  //  • expandiendo el panel → se conserva el contexto de apertura (el agente
  //    tiene que seguir sabiendo qué es "esto") y el panel queda abierto para
  //    cuando el usuario vuelva a otra pantalla;
  //  • por la sidebar o por la URL → no hay contexto ni panel previo, así que
  //    se limpia el uno y no se abre el otro.
  const { state: estadoNavegacion } = useLocation();
  const vengoDelPanel = vieneDelPanel(estadoNavegacion);
  const entrarAPantalla = chat.entrarAPantalla;
  useEffect(() => {
    entrarAPantalla({ conservarContexto: vengoDelPanel });
    // Solo al montar: volver a correrlo en cada render pisaría el contexto de
    // apertura todo el tiempo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un gráfico recién llegado se abre solo, pero SOLO en desktop: acá la
  // columna de la derecha aparece al lado de la conversación y se lee como
  // parte de la respuesta. En mobile el panel es una hoja a pantalla completa
  // y abrirla sola le taparía el chat al usuario en mitad de la lectura — ahí
  // se abre únicamente si toca la tarjeta.
  //
  // QUÉ gráfico lo decide `graficoQueSeAbreSolo`, que es puro y está testeado.
  // La cuenta no es "el más nuevo": si el usuario llegó desde el panel lateral
  // tocando la tarjeta de un gráfico viejo, ESE es el que quiere ver y abrirle
  // el último lo mandaría a cualquier lado.
  const ofrecidoRef = useRef<string | null | undefined>(undefined);
  const idMasNuevo = ultimoGrafico(chat.mensajes)?.id ?? null;
  // El gráfico abierto se lee por ref y no entra en las dependencias: es un
  // dato de entrada de la decisión, no un disparador. Si estuviera en las
  // deps, abrir uno volvería a correr el efecto en el acto.
  const abiertoRef = useRef<string | null>(chat.graficoActivoId);
  abiertoRef.current = chat.graficoActivoId;
  const abrirGrafico = chat.abrirGrafico;
  useEffect(() => {
    // En mobile no se registra NADA a propósito: así, si el usuario agranda la
    // ventana, la decisión se toma recién ahí, con su elección ya hecha.
    if (!esEscritorio) return;
    const aAbrir = graficoQueSeAbreSolo({
      idMasNuevo,
      idAbierto: abiertoRef.current,
      idYaOfrecido: ofrecidoRef.current,
    });
    ofrecidoRef.current = idMasNuevo;
    if (aAbrir) abrirGrafico(aAbrir);
  }, [idMasNuevo, esEscritorio, abrirGrafico]);

  return (
    // El alto disponible NO es 100dvh en mobile: `DashboardLayout` le mete al
    // `<main>` un padding-top de `env(safe-area-inset-top) + 3.5rem + 0.75rem`
    // para el header flotante, y en desktop lo apaga (`md:pt-0`). Restarlo acá
    // es lo que deja la barra de entrada pegada abajo sin que la pantalla
    // scrollee de más. En desktop el panel de contenido es `fixed` de top-0 a
    // bottom-0 (ADR-171), así que 100dvh es exactamente el alto disponible.
    <div className="flex h-[calc(100dvh-env(safe-area-inset-top)-4.25rem)] overflow-hidden md:h-[100dvh]">
      {/* `min-w-0`: sin esto la columna de la conversación se niega a
          achicarse cuando aparece el panel de la derecha (contenido flex con
          texto largo) y termina desbordando. */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center justify-between gap-3 px-4 sm:px-6">
          <div className="min-w-0">
            {/* La etiqueta del contexto también se muestra acá (y no sólo en el
                header del panel): al expandir una conversación abierta desde
                una pantalla, el usuario tiene que seguir viendo de qué estamos
                hablando. */}
            <h1 className="truncate text-[16px] font-semibold text-brand-black">
              Chat con Atlas
              {chat.contexto?.pantalla && (
                <span className="ml-2 text-[13px] font-medium text-[#02062B]/[0.45]">
                  {chat.contexto.pantalla}
                </span>
              )}
            </h1>
          </div>
          <button
            type="button"
            onClick={chat.reiniciar}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[#02062B]/[0.1] bg-white py-2 px-4 text-[13px] font-medium text-brand-black transition-colors hover:border-[#1644EB]/[0.35] hover:text-brand-primary"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" />
            Conversación nueva
          </button>
        </header>

        <ConversacionChatAtlas
          vistaGrafico="panel-derecho"
          onAbrirGrafico={chat.abrirGrafico}
        />
      </div>

      <PanelGraficosChat />
    </div>
  );
};

export default PantallaChatAtlas;
